/* Fast Size · motor de comparação (sem DOM, testável em Node).
   Regras gerais apenas: compara medidas da peça pousada (largura × comprimento)
   em cada dimensão, em separado. Não usa perfis de calibração nem fórmulas
   altura/peso → tamanho. */
const FitEngine = (() => {
  const ALGO = 'fastsize-2026-09-29-v1';
  const ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL'];
  const r1 = x => Math.round(x * 10) / 10;
  const _t = (s, o) => { let r = (typeof window !== 'undefined' && window.__) ? window.__(s) : s; if (o) for (const k in o) r = r.split('{' + k + '}').join(o[k]); return r; };

  /* ---- entradas ---- */
  function num(v) {
    if (v === null || v === undefined) return NaN;
    const s = String(v).trim().replace(/\s*(cm|kg)$/i, '').replace(',', '.');
    return /^\d+(\.\d+)?$/.test(s) ? parseFloat(s) : NaN;
  }
  // Valida uma medida de peça pousada. Devolve {value} ou {error}.
  function garmentMeasure(raw, dim) {
    if (raw === null || raw === undefined || String(raw).trim() === '') return { error: 'empty' };
    const v = num(raw);
    if (isNaN(v) || v <= 0) return { error: 'nan' };
    if (v >= 250) return { error: 'mm' };                       // p. ex. 560 = milímetros
    if (dim === 'w') {
      if (v < 30) return { error: 'small' };                     // provavelmente polegadas ou valor errado
      if (v > 80 && v < 250) return { error: 'perimeter' };      // parece a volta toda, não axila a axila
    } else {
      if (v < 40) return { error: 'small' };
      if (v > 110) return { error: 'range' };
    }
    return { value: r1(v) };
  }
  function height(raw) {
    if (raw === null || raw === undefined || String(raw).trim() === '') return { error: 'empty' };
    const v = num(raw);
    if (isNaN(v)) return { error: 'nan' };
    if (v >= 1.2 && v <= 2.3) return { error: 'meters' };
    if (v < 120 || v > 230) return { error: 'range' };
    return { value: v };
  }
  function weight(raw) {
    if (raw === null || raw === undefined || String(raw).trim() === '') return { value: null }; // opcional
    const v = num(raw);
    if (isNaN(v)) return { error: 'nan' };
    if (v < 30 || v > 250) return { error: 'range' };
    return { value: v };
  }

  /* ---- tabelas ---- */
  const model = (T, m) => T.models[m];
  const row = (T, m, size) => (model(T, m) ? model(T, m).rows.find(r => r[0] === size) : null) || null;
  const hasSize = (T, m, size) => !!row(T, m, size);

  /* ---- diferenças (sinal: + mais largo/comprido, − mais estreito/curto) ---- */
  const fmt = d => String(Math.abs(d)).replace('.', ',');
  function diffText(dw, dl) {
    const w = dw === 0 ? _t('mesma largura') : _t('{n} cm de largura', { n: (dw > 0 ? '+' : '−') + fmt(dw) });
    const l = dl === 0 ? _t('mesmo comprimento') : _t('{n} cm de comprimento', { n: (dl > 0 ? '+' : '−') + fmt(dl) });
    return { wTxt: w, lTxt: l, both: `${w} · ${l}` };
  }
  function diffWords(dw, dl) {
    const w = dw === 0 ? _t('mesma largura') : _t(dw > 0 ? '{n} cm mais larga' : '{n} cm mais estreita', { n: fmt(dw) });
    const l = dl === 0 ? _t('mesmo comprimento') : _t(dl > 0 ? '{n} cm mais comprida' : '{n} cm mais curta', { n: fmt(dl) });
    return `${w}, ${l}`;
  }
  // Compara dois tamanhos (podem ser de modelos diferentes). Só descreve dimensões.
  function compareSizes(T, mA, sA, mB, sB) {
    const a = row(T, mA, sA), b = row(T, mB, sB);
    if (!a || !b) return { error: !a ? 'noSizeA' : 'noSizeB' };
    const dw = r1(b[1] - a[1]), dl = r1(b[2] - a[2]);
    return Object.assign({ dw, dl, crossModel: mA !== mB }, diffText(dw, dl));
  }

  /* ---- comparação principal ---- */
  const OKW = { igual: () => true, naoMaisLarga: d => d <= 0, maisLarga: d => d > 0, maisJusta: d => d < 0 };
  const OKL = { igual: () => true, naoMaisCurta: d => d >= 0, maisComprida: d => d > 0, maisCurta: d => d < 0 };
  // ref = {w,l} em cm. Filtra pelas restrições; fica com os tamanhos que nenhum outro
  // supera nas DUAS dimensões ao mesmo tempo (proximidade |dw| e |dl|, nunca somadas).
  function compare(T, m, ref, wantW = 'igual', wantL = 'igual') {
    const all = model(T, m).rows.map(([size, w, l]) => ({ size, w, l, dw: r1(w - ref.w), dl: r1(l - ref.l) }));
    const pass = all.filter(r => OKW[wantW](r.dw) && OKL[wantL](r.dl));
    const A = Math.abs;
    const options = pass.filter(a => !pass.some(b => b !== a && A(b.dw) <= A(a.dw) && A(b.dl) <= A(a.dl) && (A(b.dw) < A(a.dw) || A(b.dl) < A(a.dl))));
    options.forEach(o => { Object.assign(o, diffText(o.dw, o.dl)); o.words = diffWords(o.dw, o.dl); });
    const status = options.length === 0 ? 'none' : options.length === 1 ? 'single' : 'between';
    return { all, options, status, wantW, wantL };
  }
  // Como a peça de referência fica + limite explícito → o que se quer em cada dimensão.
  function feelToWant(feelW, feelL, limit) {
    const wantW = feelW === 'apertada' ? 'maisLarga' : feelW === 'larga' ? 'maisJusta' : limit === 'naoMaisLarga' ? 'naoMaisLarga' : 'igual';
    const wantL = feelL === 'curta' ? 'maisComprida' : feelL === 'comprida' ? 'maisCurta' : limit === 'naoMaisCurta' ? 'naoMaisCurta' : 'igual';
    const conflict = (feelW === 'apertada' && limit === 'naoMaisLarga') || (feelL === 'comprida' && limit === 'naoMaisCurta');
    return { wantW, wantL, conflict };
  }
  const familyOf = (T, m) => model(T, m).family;
  const sleeveNote = m => (m === 'sweat' || m === 'hoodie' ? ['noSleeves'] : []);

  /* ---- Percurso A: já tenho uma TRATANTE ---- */
  function fromTratante(T, target, a) {
    const r = row(T, a.ownModel, a.ownSize);
    if (!r) return { status: 'needMore', notes: ['noSizeInTable'], validated: false, path: 'tratante' };
    const want = feelToWant(a.feelW, a.feelL, a.limit);
    const base = { ref: { w: r[1], l: r[2] }, path: 'tratante', validated: true, algo: ALGO, tableVersion: T.version };
    const notes = ['usedGarment'].concat(a.ownModel !== target ? ['crossModel'] : [], sleeveNote(target));
    if (want.conflict) return Object.assign(base, { status: 'conflict', options: [], all: [], notes });
    return Object.assign(base, compare(T, target, base.ref, want.wantW, want.wantL), { notes });
  }
  /* ---- Percurso B: comparar com uma peça minha ---- */
  function fromGarment(T, target, a) {
    const w = garmentMeasure(a.refW, 'w'), l = garmentMeasure(a.refL, 'l');
    if (w.error || l.error) return { status: 'invalid', errors: { w: w.error, l: l.error }, path: 'peca' };
    const want = feelToWant(a.feelW, a.feelL, a.limit);
    const base = { ref: { w: w.value, l: l.value }, path: 'peca', validated: true, algo: ALGO, tableVersion: T.version };
    const cross = a.refType && a.refType !== familyOf(T, target);
    const notes = ['flatOnly'].concat(cross ? ['crossModel'] : [], sleeveNote(target));
    if (want.conflict) return Object.assign(base, { status: 'conflict', options: [], all: [], notes });
    return Object.assign(base, compare(T, target, base.ref, want.wantW, want.wantL), { notes });
  }
  /* ---- Percurso C: indicação rápida sem medir (NÃO validada) ---- */
  function quick(T, target, a) {
    const notes = ['notValidated'];
    const h = height(a.h), w = weight(a.w);
    const errors = {};
    if (a.h !== undefined && a.h !== null && String(a.h).trim() !== '' && h.error) errors.h = h.error;
    if (w.error) errors.w = w.error;
    if (Object.keys(errors).length) return { status: 'invalid', errors, path: 'rapido', validated: false };
    if (h.value) notes.push('hwContextOnly');
    const base = { path: 'rapido', validated: false, algo: ALGO, tableVersion: T.version };
    if (!a.habit || a.habit === 'naoSei') return Object.assign(base, { status: 'needMore', options: [], notes: notes.concat('noHabit') });
    const r = row(T, target, a.habit);
    if (!r) return Object.assign(base, { status: 'needMore', options: [], notes: notes.concat((model(T, target).notMade || []).includes(a.habit) ? 'notMade' : 'noSizeInTable') });
    notes.push('habitAssumption');
    const wantL = a.lenProb === 'curta' ? 'maisComprida' : a.lenProb === 'comprida' ? 'maisCurta' : 'igual';
    const res = compare(T, target, { w: r[1], l: r[2] }, 'igual', wantL);
    if (a.fit === 'folgado' && target === 'oversize') notes.push('oversizeLoose');
    // Factos sobre o tamanho vizinho quando pede mais folga/mais justo (sem mudar a indicação).
    let neighbour = null;
    if ((a.fit === 'folgado' && target !== 'oversize') || a.fit === 'justo') {
      const rows = model(T, target).rows, i = rows.findIndex(x => x[0] === (res.options[0] || {}).size);
      const j = a.fit === 'folgado' ? i + 1 : i - 1;
      if (i >= 0 && rows[j]) { const c = compareSizes(T, target, rows[i][0], target, rows[j][0]); neighbour = { size: rows[j][0], dw: c.dw, dl: c.dl, both: c.both, dir: a.fit }; }
    }
    return Object.assign(base, res, { ref: { w: r[1], l: r[2] }, notes: notes.concat(sleeveNote(target)), neighbour });
  }

  /* ---- disponibilidade: só depois da adequação; nunca muda a indicação ---- */
  function withStock(result, sizes) {
    if (!sizes || !result.options) return result;
    result.options.forEach(o => { const s = sizes.find(x => x[0] === o.size); o.available = s ? !!s[1] : null; });
    return result;
  }

  /* ---- registo de provas (validação futura) ---- */
  function trialRecord(o) {
    return {
      participante: o.participante || null, conjunto: o.conjunto || null, modelo: o.modelo, produto: o.produto || null,
      versao_tabela: o.versao_tabela || null, tamanho_experimentado: o.tamanho, altura_cm: o.altura ?? null, peso_kg: o.peso ?? null,
      ref_largura_cm: o.refW ?? null, ref_comprimento_cm: o.refL ?? null, ajuste_pretendido: o.ajuste || null,
      largura: o.largura || null, comprimento: o.comprimento || null, mangas: o.mangas || null,
      tamanho_preferido: o.preferido || null, recomendacao: o.recomendacao || null, versao_algoritmo: o.algo || ALGO
    };
  }

  return { ALGO, ORDER, num, garmentMeasure, height, weight, row, hasSize, diffText, diffWords, compareSizes, compare, feelToWant, fromTratante, fromGarment, quick, withStock, trialRecord };
})();
if (typeof module !== 'undefined' && module.exports) module.exports = FitEngine;
